import { addNotification, useDispatch } from '@ggs/store'
import { useConfirmDialog } from '@ggs/ui/hooks'
import { useI18n } from '@ggs/i18n'
import { useDrupalApi } from './index'
import { noop, delay } from 'lodash'

/**
 * @typedef {Object} DeleteProjectFlowHook
 * @property {JSX.Element} ConfirmDialogComponent
 * @property {function} confirmProjectDeletion
 */

/**
 * @typedef {Object} DeleteProjectFlowProps
 * @property {string} projectId
 * @property {function=} successProjectDeleteCallback
 */

/**
 * Delete project confirm UI / process flow features hook.
 * @param {DeleteProjectFlowProps} props
 * @return {DeleteProjectFlowHook}
 */
const useDeleteProjectFlow = ({ projectId, successProjectDeleteCallback = noop }) => {
  const { t } = useI18n()
  const { deleteProject } = useDrupalApi()
  const dispatch = useDispatch()
  const isStorybook = `${process.env.STORYBOOK}` === 'yes'

  /**
   * Process deletion of current project.
   */
  const processDeleteProject = async () => {
    try {
      await deleteProject(projectId)
      dispatch(
        addNotification('delete_project_success', 'success', t('Project was successfully deleted.'))
      )

      successProjectDeleteCallback()
      // Allow notifications are displayed and post delete actions happen with delayed refresh page.
      if (!isStorybook) {
        delay(() => {
          window.location.reload()
        }, 2000)
      }
    } catch (e) {
      dispatch(addNotification('delete_project_failed', 'error', t('Project deletion failed.')))
    }
  }

  const { handleOpenClick: confirmProjectDeletion, ConfirmDialogComponent } = useConfirmDialog({
    acceptCallback: async () => {
      await processDeleteProject()
    },
    cancelCallback: () => {},
    acceptLabel: t('Delete Project'),
    cancelLabel: t('Cancel'),
    actionMessage: t('Please confirm that you want to delete your project'),
    dialogTitle: t('Delete this Project?'),
    submitBtnClasses: 'delete-project-btn--submit',
    cancelBtnClasses: 'delete-project-btn--cancel'
  })

  return {
    ConfirmDialogComponent,
    confirmProjectDeletion,
  }
}

export default useDeleteProjectFlow
